"use client"

import { ClientAvatarWithNameAndTitle, CreativeAvatarWithNameAndTitle, JobTitleColorised } from "@/components"
import { CollapsibleText } from "@/components/CollapsibleText"
import { Image, Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import {
  JobApplicationStatus,
  JobInvitationStatus,
  JobPostingPayType,
  UnredactedCompactJobsFeedItemFragmentDoc,
} from "@/gql/graphql"
import { formatDateDiffDisplay } from "@/utils/formatDateDisplay"
import clsx from "clsx"
import { JobStatusTag, JobStatusTagStatus } from "@/components/JobStatusTag"

graphql(`
  fragment UnredactedCompactJobsFeedItem on JobPosting {
    id
    activeInteractionForCreative(creativeId: $creativeId) {
      ... on JobApplication {
        id
        __typename
        clientHasInteractedWith
        applicationStatus: status
        chat {
          id
        }
      }
      ... on JobInvitation {
        id
        __typename
        invitationStatus: status
      }
    }
    payType
    publishedAt
    payString
    description
    owner {
      id
      __typename
      ...CreativeAvatarWithNameAndTitle
      ...ClientAvatarWithNameAndTitle
    }
    coverImage {
      id
      thumbnailUrl645: thumbnailUrl(width: 645)
    }

    ...JobTitleColorised
  }
`)

interface Props {
  jobPost: FragmentType<typeof UnredactedCompactJobsFeedItemFragmentDoc> | null | undefined
}

export const UnredactedCompactJobsFeedItem: React.FC<Props> = props => {
  const jobPost = useFragment(UnredactedCompactJobsFeedItemFragmentDoc, props.jobPost)

  const ownerTypename = jobPost?.owner?.__typename
  const owner =
    ownerTypename &&
    ["IndividualClient", "IndividualCreative", "RedactedIndividualClient", "RedactedIndividualCreative"].includes(
      ownerTypename
    )
      ? jobPost?.owner
      : null
  const ownerIsClient = owner?.__typename === "IndividualClient"
  const ownerIsCreative = owner?.__typename === "IndividualCreative"

  const image = jobPost?.coverImage?.thumbnailUrl645

  const isPayTbd = jobPost?.payString === "$TBD"
  const activeInteractionForCreative = jobPost?.activeInteractionForCreative
  const hasApplied =
    activeInteractionForCreative &&
    activeInteractionForCreative.__typename === "JobApplication" &&
    activeInteractionForCreative?.applicationStatus === JobApplicationStatus.Submitted

  const hasViewed = hasApplied ? activeInteractionForCreative.clientHasInteractedWith : false
  const isInvited =
    activeInteractionForCreative &&
    activeInteractionForCreative.__typename === "JobInvitation" &&
    activeInteractionForCreative?.invitationStatus === JobInvitationStatus.Sent

  const hasChat =
    activeInteractionForCreative &&
    activeInteractionForCreative.__typename === "JobApplication" &&
    activeInteractionForCreative?.chat?.id

  const isUnpaid = jobPost?.payType === JobPostingPayType.Unpaid

  const publishedAtFormatted = jobPost?.publishedAt ? formatDateDiffDisplay(jobPost.publishedAt, true) : ""

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full flex-col">
        {ownerIsClient ? (
          <ClientAvatarWithNameAndTitle size="sm" variant="sm" client={owner} canClick={false} subtitleMaxLines={1} />
        ) : null}
        {ownerIsCreative ? (
          <CreativeAvatarWithNameAndTitle
            size="sm"
            variant="sm"
            canClick={false}
            creative={owner}
            subtitleMaxLines={1}
          />
        ) : null}
      </div>

      <div className="flex w-full flex-row items-end justify-between">
        <div className="flex w-full flex-row">
          <div className="flex w-full flex-col pr-5">
            <JobTitleColorised
              jobPost={jobPost}
              textVariant="body1"
              commonWordColor="text-fg-2"
              focusWordColor="text-fg-1"
            />
            {jobPost?.description && (
              <CollapsibleText
                text={jobPost?.description}
                maxLines={2}
                className="mt-2 text-fg-2"
                textVariant="body3"
                moreLessTextVariant="body2"
              />
            )}
          </div>
          {image && (
            <div className="relative flex size-[100px] shrink-0 overflow-hidden rounded-md bg-bg-3">
              <Image
                srcSetUrls={{
                  sm: image,
                }}
                alt="Cover image"
                className="h-full w-full object-cover"
              />
            </div>
          )}
        </div>
        <div />
      </div>

      <div className="flex flex-row items-center justify-between">
        <Text variant="caption" className="text-fg-2">
          {publishedAtFormatted}
        </Text>
        {jobPost?.payString && !isPayTbd ? (
          <div className="flex flex-shrink-0 flex-row items-center gap-2 rounded-md bg-bg-2 px-2 py-2">
            {hasChat ? (
              <JobStatusTag status={JobStatusTagStatus.ChatStarted} />
            ) : hasViewed ? (
              <JobStatusTag status={JobStatusTagStatus.Viewed} />
            ) : hasApplied ? (
              <JobStatusTag status={JobStatusTagStatus.Applied} />
            ) : (
              isInvited && <JobStatusTag status={JobStatusTagStatus.Invited} />
            )}
            <Text variant="body2" className={clsx(isUnpaid ? "text-fg-3" : "text-fg-1")}>
              {jobPost?.payString}
            </Text>
          </div>
        ) : (
          <div>
            {hasChat ? (
              <JobStatusTag status={JobStatusTagStatus.ChatStarted} />
            ) : hasViewed ? (
              <JobStatusTag status={JobStatusTagStatus.Viewed} />
            ) : hasApplied ? (
              <JobStatusTag status={JobStatusTagStatus.Applied} />
            ) : (
              isInvited && <JobStatusTag status={JobStatusTagStatus.Invited} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
